import React, { useEffect, useState, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { DriverStatus } from '../../types/driver';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { DriverFormData, Driver } from '../../types/driver';
import { createDriver, updateDriver, getDriver } from '../../services/driverService';
import { formatPhoneNumber, validatePhoneNumber, BURKINA_FASO_CITIES } from '../../utils/regionalUtils';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';

interface DriverFormProps {
  isEdit?: boolean;
}

const DriverForm: React.FC<DriverFormProps> = ({ isEdit = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<DriverFormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    status: 'Active',
    vehicleType: '',
    vehicleNumber: '',
    licenseNumber: '',
    currentLocation: '',
  });
  
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      const fetchDriver = async () => {
        try {
          const driver = await getDriver(id);
          setFormData({
            name: driver.name,
            email: driver.email,
            phone: driver.phone || '',
            password: '', // Don't pre-fill password for security
            status: driver.status,
            vehicleType: driver.vehicleType || '',
            vehicleNumber: driver.vehicleNumber || '',
            licenseNumber: driver.licenseNumber || '',
            currentLocation: driver.currentLocation || '',
          });
        } catch (error) {
          console.error('Error fetching driver:', error);
          navigate('/drivers');
        } finally {
          setLoading(false);
        }
      };

      fetchDriver();
    }
  }, [isEdit, id, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required', { field: t('drivers.name') });
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('validation.required', { field: t('email') });
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.email');
    }
    
    if (!isEdit && !formData.password) {
      newErrors.password = t('validation.required', { field: t('password') });
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = t('validation.passwordLength', { min: 8 });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Get the raw input value
    let value = e.target.value;
    
    // Remove any existing formatting
    const digits = value.replace(/\D/g, '');
    
    // Limit to 8 digits (Burkina Faso local number length)
    if (digits.length > 8) {
      return;
    }
    
    // Format the digits as XX XX XX XX
    let formatted = '';
    for (let i = 0; i < digits.length; i++) {
      if (i > 0 && i % 2 === 0) {
        formatted += ' ';
      }
      formatted += digits[i];
    }
    
    // Update the form data with the formatted number
    setFormData(prev => ({ ...prev, phone: formatted }));
    
    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError('');
    }
  }, [phoneError]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Skip if it's a phone field (handled separately)
    if (name === 'phone') {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = (e: SelectChangeEvent<DriverStatus>) => {
    setFormData({
      ...formData,
      status: e.target.value as DriverStatus
    });
  };

  const handleVehicleTypeChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      vehicleType: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    setPhoneError('');
    
    // Validate phone number if provided
    if (formData.phone) {
      // Remove all non-digit characters for validation
      const digits = formData.phone.replace(/\D/g, '');
      
      // Check if we have exactly 8 digits
      if (digits.length !== 8 || !/^[67]/.test(digits)) {
        setPhoneError(t('validation.invalidPhone'));
        return;
      }
    }
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    try {
      // Prepare the data to send
      const dataToSend: Partial<DriverFormData> = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        // The phone number is already in the correct format from the input
        phone: formData.phone ? formData.phone.replace(/\D/g, '') : '',
        status: formData.status,
      };
      
      // Add optional fields if they exist
      if (formData.vehicleType) dataToSend.vehicleType = formData.vehicleType.trim();
      if (formData.vehicleNumber) dataToSend.vehicleNumber = formData.vehicleNumber.trim();
      if (formData.licenseNumber) dataToSend.licenseNumber = formData.licenseNumber.trim();
      if (formData.currentLocation) dataToSend.currentLocation = formData.currentLocation.trim();
      
      // Only include password if it's a new driver or being changed
      if (!isEdit || formData.password) {
        dataToSend.password = formData.password;
      }
      
      console.log('Submitting form data:', { isEdit, id, dataToSend });
      
      if (isEdit && id) {
        await updateDriver(id, dataToSend);
      } else {
        await createDriver(dataToSend as DriverFormData);
      }
      
      // Show success message or navigate away
      // You might want to add a success notification here
      navigate('/drivers');
    } catch (error: any) {
      console.error('Error saving driver:', error);
      
      // Handle specific error cases
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 409) {
          setErrors({ email: t('validation.emailExists') });
        } else if (status === 400 && data.errors) {
          // Handle validation errors from the server
          const serverErrors: Record<string, string> = {};
          Object.keys(data.errors).forEach(key => {
            serverErrors[key] = data.errors[key][0]; // Get the first error message
          });
          setErrors(serverErrors);
        } else {
          setErrors({ submit: data.message || t('errors.saveData') });
        }
      } else {
        setErrors({ submit: error.message || t('errors.saveData') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">
          {isEdit ? t('drivers.editDriver') : t('drivers.addDriver')}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          {t('common.back')}
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t('drivers.form.name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label={t('email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                required
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('drivers.form.phone')}
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                error={!!errors.phone || !!phoneError}
                helperText={
                  errors.phone || 
                  phoneError || 
                  (formData.phone ? '' : t('validation.phoneFormat', { example: '70 12 34 56' }))
                }
                margin="normal"
                placeholder="70 12 34 56"
                inputProps={{
                  inputMode: 'tel',
                  autoComplete: 'tel',
                  maxLength: 11, // 8 digits + 3 spaces
                  style: { textAlign: 'left' }
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      px: 1,
                      color: 'text.secondary',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      height: '100%',
                      mr: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }}>
                      +226
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth margin="normal" error={!!errors.status}>
                <InputLabel id="status-label">{t('drivers.status')}</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleStatusChange}
                  label={t('drivers.status')}
                >
                  <MenuItem value="Active">{t('drivers.statuses.active')}</MenuItem>
                  <MenuItem value="Inactive">{t('drivers.statuses.inactive')}</MenuItem>
                  <MenuItem value="On Delivery">{t('drivers.statuses.on_delivery')}</MenuItem>
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            </Grid>
            {!isEdit && (
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  label={t('drivers.form.password')}
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  margin="normal"
                  required={!isEdit}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('drivers.form.vehicleType')}</InputLabel>
                <Select
                  name="vehicleType"
                  value={formData.vehicleType}
                  onChange={handleVehicleTypeChange}
                  label={t('drivers.form.vehicleType')}
                >
                  <MenuItem value="car">{t('drivers.vehicleTypes.car')}</MenuItem>
                  <MenuItem value="motorcycle">
                    {t('drivers.vehicleTypes.motorcycle')}
                  </MenuItem>
                  <MenuItem value="bicycle">
                    {t('drivers.vehicleTypes.bicycle')}
                  </MenuItem>
                  <MenuItem value="truck">
                    {t('drivers.vehicleTypes.truck')}
                  </MenuItem>
                  <MenuItem value="van">{t('drivers.vehicleTypes.van')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t('drivers.form.vehicleNumber')}
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t('drivers.form.licenseNumber')}
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="location-label">{t('drivers.form.currentLocation')}</InputLabel>
                <Select
                  labelId="location-label"
                  name="currentLocation"
                  value={formData.currentLocation}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentLocation: e.target.value }))}
                  label={t('drivers.form.currentLocation')}
                >
                  {BURKINA_FASO_CITIES.map(city => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/drivers')}
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                  disabled={submitting}
                >
                  {submitting ? t('common.saving') : t('common.save')}
                </Button>
              </Box>
            </Grid>
            {errors.submit && (
              <Grid item xs={12}>
                <Typography color="error" align="center">
                  {errors.submit}
                </Typography>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default DriverForm;
